import React, { useState, useEffect } from "react";
import { Card, ListGroup, Form, Dropdown } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import GenericFHIRViewerModal from "./GenericFHIRViewerModal";
import styles from "./Card.module.css";
import { fetchProvider } from "../services/providerService";
import { useDispatch, useStore, useSelector } from "react-redux";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import tooltipRegistry from "../tooltips/tooltipRegistry";

// ✅ Import gender-specific icons from assets
import DoctorFemaleIcon from "../assets/Doctor_Female_96.png";
import DoctorMaleIcon from "../assets/Doctor_Male_96.png";

function ProviderCard({
  title,
  providerData,
  showSearch,
  inContext,
  onSelectProvider,
  fhirClient
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedProviders, setSearchedProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(providerData || null);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalProviderDetails, setModalProviderDetails] = useState(null);
  const handleModalClose = () => setShowModal(false);
  const dispatch = useDispatch();
  const store = useStore();
  const providers = useSelector(state => state.providers);

  useEffect(() => {
    if (providerData?.name?.[0]) {
      const name = `${providerData.name[0].given?.join(" ") || ""} ${providerData.name[0].family || ""}`.trim();
      setSearchTerm(name);
    }
  }, [providerData]);

  const handleModalOpen = async () => {
    const providerId = displayProvider?.id;

    if (!providerId) {
      console.warn("Provider ID not available");
      setModalProviderDetails(null);
      setShowModal(true);
      return;
    }

    let provider = providers[providerId];

    if (!provider) {
      try {
        provider = await fetchProvider(providerId, fhirClient, store.getState, dispatch);
      } catch (err) {
        console.error("Failed to fetch provider via service", err);
      }
    }

    setModalProviderDetails(provider || null);
    setShowModal(true);
  };

  async function handleSearchByName() {
    if (!searchTerm.trim()) return setError("Please enter a provider name.");
    if (!fhirClient) return setError("FHIR Client is not initialized.");

    try {
      const searchResults = await fhirClient.request(`Practitioner?name=${encodeURIComponent(searchTerm)}`);
      const found = searchResults.entry?.map(e => e.resource) || [];
      setSearchedProviders(found);
      setError(found.length ? "" : "No providers found.");
    } catch (err) {
      console.error("Error fetching provider data:", err);
      setError("Error fetching provider data.");
    }
  }

  function handleSelectProvider(provider) {
    const fullName = provider.name?.[0]?.given?.join(" ") + " " + provider.name?.[0]?.family;
    setSearchTerm(fullName);
    setSelectedProvider(provider);
    setSearchedProviders([]);
    if (onSelectProvider) {
      onSelectProvider(provider);
    }
  }

  const displayProvider = selectedProvider || providerData || {
    name: [{ given: [""], family: "" }],
    resourceType: "N/A",
    gender: "N/A",
    active: "Unknown",
    qualification: [],
    address: [{ line: [""], city: "", state: "", postalCode: "" }],
    telecom: [{ system: "phone", value: "N/A" }]
  };

  const providerAddress = displayProvider.address?.[0];
  const fullAddress = [
    ...(providerAddress?.line || []),
    providerAddress?.city,
    providerAddress?.state,
    providerAddress?.postalCode
  ].filter(Boolean).join(", ");

  const googleMapsUrl = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;

  // ✅ Select gender-specific icon
  const gender = displayProvider.gender?.toLowerCase();
  const doctorIcon = gender === "female" ? DoctorFemaleIcon : DoctorMaleIcon;

  return (
    <>
      <Card style={{ width: "100%", marginTop: "5px", minHeight: "275px" }}>
        <Card.Header
          className={`d-flex justify-content-between align-items-center ${inContext ? styles.inContextCardHeader : styles.cardHeader}`}
        >
          <h5 className="mb-0">{title}</h5>

          <OverlayTrigger placement="bottom" overlay={tooltipRegistry.providerCard}>
            <img
              src={doctorIcon}
              alt="Doctor Icon"
              style={{ cursor: "pointer", height: "32px", width: "32px" }}
              onClick={handleModalOpen}
            />
          </OverlayTrigger>
        </Card.Header>

        <Card.Body>
          {error && <p style={{ color: "red" }}>{error}</p>}

          <ListGroup variant="flush">
            <ListGroup.Item className={`d-flex align-items-center ${styles.listItem}`}>
              <strong>Name:&nbsp;</strong>
              {showSearch ? (
                <div className="d-flex align-items-center ms-2" style={{ flex: 1, position: "relative" }}>
                  <Form.Control
                    type="text"
                    placeholder="Find a Provider"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearchByName();
                      }
                    }}
                    className="me-2"
                  />
                  <FaSearch
                    style={{ cursor: "pointer", color: "#007bff", marginLeft: "5px" }}
                    onClick={handleSearchByName}
                  />
                  {searchedProviders.length > 0 && (
                    <Dropdown.Menu show style={{ position: "absolute", width: "100%", top: "100%", left: 0, zIndex: 1000 }}>
                      {searchedProviders.map((provider) => (
                        <Dropdown.Item key={provider.id} onClick={() => handleSelectProvider(provider)}>
                          {provider.name?.[0]?.given?.join(" ")} {provider.name?.[0]?.family} :{" "}
                          {provider.address?.[0]?.city}, {provider.address?.[0]?.state}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  )}
                </div>
              ) : (
                <>
                  {displayProvider.name?.[0] &&
                    (displayProvider.name[0].given?.[0]?.trim() || displayProvider.name[0].family?.trim())
                    ? `Dr. ${displayProvider.name[0].given.join(" ")} ${displayProvider.name[0].family}`
                    : "Unknown"}
                </>
              )}
            </ListGroup.Item>

            <ListGroup.Item className={styles.listItem}><strong>Provider Type:</strong> {displayProvider.resourceType}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Gender:</strong> {displayProvider.gender}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}>
              <strong>Active:</strong>{" "}
              {displayProvider.active === "Unknown"
                ? "Unknown"
                : displayProvider.active
                  ? "Yes"
                  : "No"}
            </ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Qualifications:</strong> {displayProvider.qualification?.map(q => q.code.text).join(", ") || "N/A"}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}>
              <strong>Address:</strong> {fullAddress || "N/A"}
              {googleMapsUrl && (
                <div className="mt-1">
                  <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">View on Google Maps</a>
                </div>
              )}
            </ListGroup.Item>
            <ListGroup.Item className={styles.listItem}>
              <strong>Phone Number:</strong> {displayProvider.telecom?.find(t => t.system === "phone")?.value}
            </ListGroup.Item>
          </ListGroup>
        </Card.Body>
      </Card>

      <GenericFHIRViewerModal
        show={showModal}
        onClose={handleModalClose}
        resource={modalProviderDetails}
        title={
          modalProviderDetails?.name?.[0]
            ? `Dr. ${modalProviderDetails.name[0].given?.join(" ")} ${modalProviderDetails.name[0].family}`
            : "Provider"
        }
      />
    </>
  );
}

export default ProviderCard;